/**
 * useCrypto Encrypt/Decrypt
 *
 * Core E2EE message encryption and decryption for DM and multi-device scenarios.
 * Extracted from useCrypto.ts for maintainability.
 *
 * Functions accept `get`/`set` from the Zustand store to avoid circular imports
 * (same pattern as useCrypto-group.ts and useCrypto-recovery.ts).
 */

import {
  receiveSession,
  restoreSession,
  toBase64,
  fromBase64,
  FALLBACK_PREKEY_ID,
  findSignedPreKeyById,
  type SerializedRatchetState,
  type CryptoSession,
} from "@kollegeplus/crypto";
import { api } from "../lib/api";
import {
  saveSession,
  saveSessionOrThrow,
  loadSession,
  deleteSession,
  getFirstDeviceKeys,
  saveDeviceKeys,
  savePeerSenderKey,
  loadPeerSenderKey,
  saveChannelRecoveryKey,
  loadChannelRecoveryKey,
  type StoredPeerSenderKey,
} from "../lib/crypto-storage";
import { e2eeLog } from "../lib/e2ee-logger";
import { reportE2eeError } from "../lib/e2ee-telemetry";
import { withSessionLock } from "../lib/session-lock";
import type { E2EEEnvelope, MultiDeviceRecipient, ServerDeviceKeys } from "@kollegeplus/shared";
import { selfEncrypt } from "./useCrypto-self";
import { deserializeDeviceKeys, resolvePreKeyWithRefresh } from "./useCrypto-helpers";

/** Re-read deviceKeys from IDB (source of truth) when the in-memory snapshot lags. */
const loadFreshDeviceKeys = async () => {
  const s = await getFirstDeviceKeys();
  return s ? deserializeDeviceKeys(s) : null;
};
import { SPK_ID_MIN_UNIQUE } from "../lib/spk-id";
import type { CryptoState } from "./useCrypto";

/**
 * Thrown when a message cannot be encrypted because a non-bot recipient has no
 * registered device yet. The caller knows the channel composition (which IDs are
 * bots) and decides whether to surface this as a user-facing error.
 */
export class RecipientNotReadyError extends Error {
  recipientUserIds: string[];

  constructor(recipientUserIds: string[]) {
    super(`Recipient(s) not ready: ${recipientUserIds.join(", ")}`);
    this.name = "RecipientNotReadyError";
    this.recipientUserIds = recipientUserIds;
  }
}

/**
 * Thrown when this device's own crypto state is known-broken (self-canary failed
 * at init, or runtime corruption was detected). A corrupt device that keeps
 * sending produces envelopes peers cannot decrypt (observed in prod: device
 * web-hUMZd4 ghash-failed on every receive yet kept sending an undecryptable
 * DM). We refuse to encrypt until the device re-initialises.
 */
export class CryptoBrokenError extends Error {
  constructor() {
    super("Local crypto state is broken — refusing to encrypt until re-init");
    this.name = "CryptoBrokenError";
  }
}

/** Throw CryptoBrokenError if the device's crypto health is flagged broken. */
export function guardCryptoHealthy(get: () => CryptoState): void {
  if (get().cryptoHealth === "broken") {
    reportE2eeError({
      phase: "send_blocked_broken",
      deviceId: get().deviceId ?? null,
      error: "encrypt refused — cryptoHealth=broken",
    });
    throw new CryptoBrokenError();
  }
}

// ─── Runtime corruption detector ──────────────────────────────────────────────
// A single broken peer causes ghash failures from ONE sender. Local corruption
// (bad IDB / ratchet state) causes failures from MANY distinct senders. When we
// see decrypt failures from >= DISTINCT_SENDER_THRESHOLD distinct senders inside
// a rolling window, we re-run the self-canary; if THAT fails too, the breakage
// is provably local → flag cryptoHealth=broken so the send guard kicks in before
// the server's cascade-detector deactivates us (and before we emit more
// undecryptable envelopes).
const RUNTIME_FAIL_WINDOW_MS = 5 * 60_000;
const DISTINCT_SENDER_THRESHOLD = 3;
const recentDecryptFailSenders = new Map<string, number>(); // senderId -> last fail ts
let lastCanaryRecheckAt = 0;

async function recordDecryptFailureAndMaybeFlagBroken(
  senderId: string,
  get: () => CryptoState,
  set: (fn: (s: CryptoState) => Partial<CryptoState>) => void,
): Promise<void> {
  try {
    const now = Date.now();
    recentDecryptFailSenders.set(senderId, now);
    // Prune stale entries outside the window.
    for (const [sid, ts] of recentDecryptFailSenders) {
      if (now - ts > RUNTIME_FAIL_WINDOW_MS) recentDecryptFailSenders.delete(sid);
    }
    if (recentDecryptFailSenders.size < DISTINCT_SENDER_THRESHOLD) return;
    // Already broken, or we re-checked very recently → nothing to do.
    if (get().cryptoHealth === "broken") return;
    if (now - lastCanaryRecheckAt < RUNTIME_FAIL_WINDOW_MS) return;
    lastCanaryRecheckAt = now;

    const state = get();
    if (!state.deviceKeys) return;
    const { runSelfCanary } = await import("../lib/crypto-canary");
    const result = await runSelfCanary(state.deviceKeys.identityKeyPair.curve25519.privateKey);
    if (!result.ok) {
      e2eeLog.error("[corruption] self-canary FAILED after cross-sender decrypt failures — flagging device broken", result.error);
      set(() => ({ cryptoHealth: "broken" }));
      reportE2eeError({
        phase: "runtime_corruption_detected",
        deviceId: state.deviceId ?? null,
        error: `distinctSenders=${recentDecryptFailSenders.size} canary=${result.error}`,
      });
    }
  } catch {
    // Detector must never throw into the decrypt path.
  }
}

/**
 * Fix 1: optional out-param for encryptForAllDevices so the caller can tell a
 * genuine "recipient has no device at all" (→ RecipientNotReadyError) apart from
 * "recipient has devices but we couldn't reach them this attempt" (→ partial
 * send + async backfill). Mutated in place during fan-out.
 */
export interface EncryptFanoutMeta {
  /** Distinct non-self (peer) devices the server returned a bundle for. */
  peerDevicesSeen: number;
  /** Non-self envelopes we actually managed to build this attempt. */
  peerEnvelopesProduced: number;
  /** (userId, deviceId) slots that exist server-side but we could NOT encrypt
   *  to this attempt — handed to the backfill path so they self-heal later. */
  skipped: Array<{ userId: string; deviceId: string }>;
  /**
   * Set when the sender's own other-device retry failed definitively (device
   * not in /users/keys refresh, or encryptOneDevice still threw). Signals the
   * caller to send with partial=true so it skips the X-Expected-Devices hash
   * check (which would 409 repeatedly because the own slot is absent from
   * recipients). Server-side backfill excludes sender-own devices, so
   * recovery falls back to the uploadMessageBackup path.
   */
  ownDeviceFailed?: boolean;
}

/**
 * Thrown when a group has no other members besides the sender, so there's no
 * recipient to encrypt to. UI should disable send and prompt the user to add
 * members rather than surfacing a generic "encryption failed" alert.
 */
export class EmptyGroupError extends Error {
  channelId: string;

  constructor(channelId: string) {
    super(`Group ${channelId} has no other members`);
    this.name = "EmptyGroupError";
    this.channelId = channelId;
  }
}

/** Apply Matrix-style firstKnownIndex / firstKnownKey to a peer sender key
 *  record being saved. Used by every receive path. Existing field wins unless
 *  the incoming distribution has a *lower* iter (better history coverage), in
 *  which case we replace and reset seenIters.
 *
 *  - On first receipt (no existing firstKnownIndex): set firstKnownKey/Index from payload.
 *  - On subsequent receipt with lower iteration: replace + reset seenIters.
 *  - On subsequent receipt with same/higher iteration: keep existing (older is more useful for history).
 *  - Seed-mode (recovery=true, iteration=0) sets firstKnownIndex=0 too, in addition to the seed field.
 */
function applyFirstKnown(
  next: { firstKnownKey?: string; firstKnownIndex?: number; seenIters?: number[]; senderKey: string; iteration: number },
  existing?: { firstKnownKey?: string; firstKnownIndex?: number; seenIters?: number[] },
): void {
  const candidateIndex = next.iteration;
  const candidateKey = next.senderKey;
  if (existing?.firstKnownKey !== undefined && existing.firstKnownIndex !== undefined) {
    if (candidateIndex < existing.firstKnownIndex) {
      next.firstKnownIndex = candidateIndex;
      next.firstKnownKey = candidateKey;
      next.seenIters = []; // reset — the ratchet root moved
    } else {
      next.firstKnownIndex = existing.firstKnownIndex;
      next.firstKnownKey = existing.firstKnownKey;
      next.seenIters = existing.seenIters ?? [];
    }
  } else {
    next.firstKnownIndex = candidateIndex;
    next.firstKnownKey = candidateKey;
    next.seenIters = [];
  }
}

// ─── encryptForAllDevices ─────────────────────────────────────────────────────

/**
 * Encrypt a message for ALL devices of multiple recipients (multi-device).
 * Also self-encrypts for our own device so we can read our own messages.
 */
export async function encryptForAllDevices(
  recipientIds: string[],
  plaintext: string,
  get: () => CryptoState,
  meta?: EncryptFanoutMeta,
): Promise<MultiDeviceRecipient[]> {
  const state = get();
  if (!state.deviceKeys || !state.deviceId) {
    throw new Error("E2EE not initialized");
  }

  // Include own userId to encrypt for own other devices
  const { useAuth } = await import("./useAuth");
  const currentUserId = useAuth.getState().user?.id;

  // Collect all userIds we need keys for (recipients + self)
  const allUserIds = [...new Set([...recipientIds, ...(currentUserId ? [currentUserId] : [])])];

  // Fetch device keys for all users at once
  const peerKeysResponse = await api.post<{
    keys: Record<string, Array<ServerDeviceKeys & { remainingPreKeys?: number }>>;
  }>("/users/keys", { userIds: allUserIds });

  // Replenish pre-keys in background if needed
  get().checkAndReplenishPreKeys().catch(() => {});

  const recipients: MultiDeviceRecipient[] = [];

  for (const userId of allUserIds) {
    const devices = peerKeysResponse.keys[userId];
    if (!devices || devices.length === 0) {
      e2eeLog.warn(`No device keys for user ${userId}, skipping`);
      continue;
    }

    for (const device of devices) {
      // Self-encrypt for our own current device (so we can read our own messages after reload)
      if (device.deviceId === state.deviceId) {
        try {
          const selfCt = await selfEncrypt(plaintext, state.deviceKeys.identityKeyPair.curve25519.privateKey);
          const envelope: E2EEEnvelope = {
            v: 1,
            type: "message",
            senderDeviceId: state.deviceId,
            payload: selfCt,
            selfEncrypted: true,
          };
          recipients.push({
            deviceId: device.deviceId,
            userId,
            ciphertext: JSON.stringify(envelope),
          });
        } catch (err) {
          e2eeLog.warn("Self-encrypt failed (non-fatal):", err);
        }
        continue;
      }

      // Fix 1: from here on `device` is a non-self DEVICE. Count it as a peer
      // device only when it belongs to a DIFFERENT user (an actual recipient,
      // not just another of our own devices) — that's what lets the caller tell
      // "peer has no device at all" from "peer unreachable this attempt".
      const isOtherUser = userId !== currentUserId;
      if (meta && isOtherUser) meta.peerDevicesSeen++;

      // SPK-only fallback telemetry: device has a signed pre-key but no OPK,
      // AND we don't have an existing session (so X3DH will actually be performed).
      // X3DH still works (3-DH without DH4), but we report the starvation event
      // so the server can track OPK pool depletion and prompt replenishment.
      // Only fires on new-session establishment to avoid noise on ongoing chats.
      if (!device.preKey) {
        const sessionKey = `${userId}:${device.deviceId}`;
        // Use live store snapshot to catch sessions written by earlier loop iterations
        const hasExistingSession = get().sessions.has(sessionKey);
        if (!hasExistingSession) {
          reportE2eeError({
            phase: "opk_pool_empty",
            // senderId = the authenticated sender (self), not the recipient whose OPK pool is empty
            senderId: currentUserId ?? userId,
            senderDeviceId: device.deviceId,
            deviceId: state.deviceId,
            error: "e2ee_x3dh_opk_starvation",
          });
          e2eeLog.warn(`OPK pool empty for device ${device.deviceId} of user ${userId} — using SPK-only X3DH`);
        }
      }

      const encryptOneDevice = async (
        targetUserId: string,
        targetDevice: ServerDeviceKeys,
      ): Promise<void> => {
        await withSessionLock(targetUserId, targetDevice.deviceId, async () => {
          const session = await get().getOrCreateSessionForDevice(targetUserId, targetDevice.deviceId, targetDevice);
          const ciphertext = session.encrypt(plaintext);
          const envelope: E2EEEnvelope = {
            v: 1,
            type: "message",
            senderDeviceId: state.deviceId!,
            payload: ciphertext,
          };
          recipients.push({
            deviceId: targetDevice.deviceId,
            userId: targetUserId,
            ciphertext: JSON.stringify(envelope),
          });
          const serializedState = session.exportState();
          const storedSession = await loadSession(targetUserId, targetDevice.deviceId);
          if (storedSession) {
            storedSession.ratchetState = JSON.stringify(serializedState);
            storedSession.updatedAt = new Date().toISOString();
            await saveSession(storedSession);
          }
        });
      };

      try {
        await encryptOneDevice(userId, device);
        if (meta && isOtherUser) meta.peerEnvelopesProduced++;
      } catch (err) {
        const errStr = err instanceof Error ? err.message : String(err);
        e2eeLog.warn(`Failed to encrypt for device ${device.deviceId} of user ${userId}:`, err);
        // Fix 1: record the unreachable peer slot so the caller can enqueue async
        // backfill instead of hard-failing the whole send.
        if (meta && isOtherUser) meta.skipped.push({ userId, deviceId: device.deviceId });
        reportE2eeError({
          phase: "fanout_envelope_dropped",
          senderId: currentUserId ?? userId,
          senderDeviceId: device.deviceId,
          deviceId: state.deviceId,
          error: `userId=${userId} firstAttempt: ${errStr.slice(0, 200)}`,
        });

        // Single-shot retry for the SENDER's OWN-user case. A silent failure
        // here used to leave the sender's other devices stuck without an
        // envelope, surfacing as "[Eigene Nachricht – Verschlüsselung aktiv]"
        // on those devices forever. Refetch /users/keys for self and retry
        // X3DH once. Peer-side recipients are not retried — they already
        // have envelopes if their fan-out succeeded earlier in the loop.
        if (currentUserId && userId === currentUserId) {
          try {
            const refresh = await api.post<{
              keys: Record<string, Array<ServerDeviceKeys & { remainingPreKeys?: number }>>;
            }>("/users/keys", { userIds: [currentUserId] });
            const refreshedDevice = refresh.keys[currentUserId]?.find(d => d.deviceId === device.deviceId);
            if (!refreshedDevice) {
              reportE2eeError({
                phase: "fanout_envelope_dropped",
                senderId: currentUserId,
                senderDeviceId: device.deviceId,
                deviceId: state.deviceId,
                error: "retry: device not in /users/keys refresh",
              });
              // Device absent from server refresh — own-device envelope cannot
              // be produced. Flag so the caller can send partial=true and skip
              // the X-Expected-Devices hash check (avoids repeated 409 loop).
              if (meta) meta.ownDeviceFailed = true;
            } else {
              await encryptOneDevice(currentUserId, refreshedDevice);
            }
          } catch (retryErr) {
            const retryStr = retryErr instanceof Error ? retryErr.message : String(retryErr);
            reportE2eeError({
              phase: "fanout_envelope_dropped",
              senderId: currentUserId,
              senderDeviceId: device.deviceId,
              deviceId: state.deviceId,
              error: `retry-failed: ${retryStr.slice(0, 200)}`,
            });
            // Retry also failed — own device won't get an envelope this send.
            // Flag for partial-send so the X-Expected-Devices hash is skipped,
            // preventing a 409 → re-encrypt → 409 loop that would abort the send
            // entirely. Own-device recovery falls back to uploadMessageBackup.
            if (meta) meta.ownDeviceFailed = true;
          }
        }
      }
    }
  }

  return recipients;
}

// ─── encryptForPeer ───────────────────────────────────────────────────────────

/**
 * Encrypt a message for a DM peer (first available device — legacy path).
 */
export async function encryptForPeer(
  peerId: string,
  plaintext: string,
  get: () => CryptoState,
): Promise<string> {
  const state = get();
  if (!state.deviceKeys || !state.deviceId) {
    throw new Error("E2EE not initialized");
  }

  // We need the peerDeviceId before we can lock — but getOrCreateSession is the
  // very thing we need to serialise. Resolve the peerDeviceId first via a quick
  // session lookup, then take the lock and re-do the canonical work inside.
  const { peerDeviceId: lockDeviceId } = await get().getOrCreateSession(peerId);

  return await withSessionLock(peerId, lockDeviceId, async () => {
    const { session, peerDeviceId } = await get().getOrCreateSession(peerId);

    // F18b: trace client-side ratchet state on every outbound encrypt.
    const preState = session.exportState();
    const isFresh = !preState.receivingChainKey;  // fresh X3DH session has no receiving chain yet
    e2eeLog.log("[F18b] encryptForPeer", {
      peerId,
      peerDeviceId,
      isFresh,
      preSendMsgNum: preState.sendingMessageNumber,
      preRecvMsgNum: preState.receivingMessageNumber,
      preHasSendChain: !!preState.sendingChainKey,
      preHasRecvChain: !!preState.receivingChainKey,
      preDhSendPub: preState.dhSendingPublic ? String(preState.dhSendingPublic).slice(0, 12) + "…" : null,
      preDhRecv: preState.dhReceivingKey ? String(preState.dhReceivingKey).slice(0, 12) + "…" : null,
    });

    // Encrypt the message
    const ciphertext = session.encrypt(plaintext);

    const postState = session.exportState();
    e2eeLog.log("[F18b] encryptForPeer.afterEncrypt", {
      peerId,
      peerDeviceId,
      postSendMsgNum: postState.sendingMessageNumber,
      postRecvMsgNum: postState.receivingMessageNumber,
      postDhSendPub: postState.dhSendingPublic ? String(postState.dhSendingPublic).slice(0, 12) + "…" : null,
    });

    // Build envelope
    const envelope: E2EEEnvelope = {
      v: 1,
      type: "message",
      senderDeviceId: state.deviceId!,
      payload: ciphertext,
    };

    // Persist updated session state
    const serializedState = session.exportState();
    const storedSession = await loadSession(peerId, peerDeviceId);
    if (storedSession) {
      storedSession.ratchetState = JSON.stringify(serializedState);
      storedSession.updatedAt = new Date().toISOString();
      await saveSession(storedSession);
    } else {
      // First message — save with AD
      const ad = new Uint8Array([
        ...state.deviceKeys!.identityKeyPair.curve25519.publicKey,
        ...session.remoteIdentityKey,
      ]);
      await saveSession({
        peerId,
        peerDeviceId,
        ratchetState: JSON.stringify(serializedState),
        associatedData: toBase64(ad),
        remoteIdentityKey: toBase64(session.remoteIdentityKey),
        updatedAt: new Date().toISOString(),
      });
    }

    return JSON.stringify(envelope);
  });
}

// ─── decryptMessage ───────────────────────────────────────────────────────────

/**
 * Decrypt a received E2EE message.
 * Returns the plaintext, or null if decryption failed / message is internal.
 */
/** Process decrypted sender key plaintext and store it per (channel, sender user, sender device).
 *  `senderDeviceId` identifies the specific device of `senderId` that generated this senderKey —
 *  critical for multi-device senders, so Laptop and Phone don't overwrite each other's slots. */
async function processSenderKeyPlaintext(
  senderId: string,
  senderDeviceId: string,
  plaintext: string,
): Promise<void> {
  // Bug D: user_backup_key envelope. Tunneled through the same X3DH outer
  // envelope. Inner shape: `{ v:1, type:"user_backup_key", payload:{key} }`.
  // key is the raw base64 AES-256 user_backup_key. Store in localStorage so
  // restoreBackupKeyFromCache() can use it immediately, then dispatch an event
  // so any pending peer_sender_key_backup decrypts can be re-attempted.
  try {
    const maybeUbk = JSON.parse(plaintext) as { v?: number; type?: string; payload?: unknown };
    if (
      maybeUbk &&
      typeof maybeUbk === "object" &&
      maybeUbk.type === "user_backup_key" &&
      maybeUbk.payload &&
      typeof maybeUbk.payload === "object"
    ) {
      const p = maybeUbk.payload as { key?: unknown };
      if (typeof p.key === "string") {
        const { saveRawBackupKeyBase64 } = await import("../lib/backup-key");
        const newKey = await saveRawBackupKeyBase64(p.key);
        // Update the Zustand store so live callers see the new backupKey immediately
        // without needing a full re-init. Dynamic import to avoid circular dep.
        const { useCrypto } = await import("./useCrypto");
        useCrypto.setState({ backupKey: newKey });
        e2eeLog.info("[Bug D] Received and stored user_backup_key from peer device", senderDeviceId);
        reportE2eeError({ phase: "backup_key_received", senderDeviceId });
        window.dispatchEvent(new CustomEvent("user-backup-key-received"));
        return;
      }
    }
  } catch {
    // Not a user_backup_key envelope — fall through
  }

  // F15e: channel-shared recovery key envelope. Tunneled through the same
  // X3DH "sender_key_x3dh" outer envelope so we don't need to invent a new
  // wire format. The discriminator lives on the inner JSON: shape
  // `{ v:1, type:"channel_recovery_key", payload:{channelId, key, keyId} }`.
  // Anything that doesn't match falls through to the existing sender-key
  // distribution path.
  try {
    const maybeInner = JSON.parse(plaintext) as { v?: number; type?: string; payload?: unknown };
    if (
      maybeInner &&
      typeof maybeInner === "object" &&
      maybeInner.type === "channel_recovery_key" &&
      maybeInner.payload &&
      typeof maybeInner.payload === "object"
    ) {
      const p = maybeInner.payload as { channelId?: unknown; key?: unknown; keyId?: unknown };
      if (
        typeof p.channelId === "string" &&
        typeof p.key === "string" &&
        typeof p.keyId === "number"
      ) {
        // Don't downgrade a higher keyId — rotation isn't implemented in v1
        // but we shouldn't let a stale envelope clobber a fresher local copy
        // if a future rotation event races with this delivery.
        const existing = await loadChannelRecoveryKey(p.channelId);
        if (existing && existing.keyId > p.keyId) {
          e2eeLog.info(
            "Ignored stale channel_recovery_key from", senderId, "channel", p.channelId,
            "incomingKeyId=", p.keyId, "storedKeyId=", existing.keyId,
          );
          return;
        }
        await saveChannelRecoveryKey({
          channelId: p.channelId,
          key: p.key,
          keyId: p.keyId,
          createdAt: new Date().toISOString(),
        });
        e2eeLog.info(
          "Stored channel recovery key from", senderId, "device", senderDeviceId,
          "channel", p.channelId, "keyId", p.keyId,
        );
        // F22c: notify the chat view so it re-attempts decryption of any messages
        // that were stuck behind the missing key (e.g. tryChannelRecoveryDecrypt
        // returned null because the key was absent; now it can succeed).
        window.dispatchEvent(new CustomEvent("channel-recovery-key-received", {
          detail: { channelId: p.channelId },
        }));
        reportE2eeError({
          phase: "recovery_key_received",
          senderDeviceId: senderDeviceId ?? null,
          error: `channelId=${p.channelId} keyId=${p.keyId}`,
        });
        return;
      }
    }
  } catch {
    // Not a JSON envelope with our discriminator — fall through to sender-key path
  }

  // F16d: bot-channel sender key. Shape:
  // `{ v:1, type:"channel_sender_key", payload:{channelId, key, keyId, iter} }`.
  // Cache key: `${channelId}:${senderId}:bot:${keyId}` — uses "bot:<keyId>" in the
  // senderDeviceId slot of peerSenderKeys to keep bot entries in a distinct ID
  // namespace from user→user sender keys (which use a real device UUID). This
  // prevents any theoretical UUID collision even though UUIDv4 collisions are
  // astronomically unlikely.
  try {
    const maybeInner = JSON.parse(plaintext) as { v?: number; type?: string; payload?: unknown };
    if (
      maybeInner &&
      typeof maybeInner === "object" &&
      maybeInner.type === "channel_sender_key" &&
      maybeInner.payload &&
      typeof maybeInner.payload === "object"
    ) {
      const p = maybeInner.payload as {
        channelId?: unknown;
        key?: unknown;
        keyId?: unknown;
        iter?: unknown;
      };
      if (
        typeof p.channelId === "string" &&
        typeof p.key === "string" &&
        typeof p.keyId === "string" &&
        typeof p.iter === "number"
      ) {
        // Use "bot:<keyId>" as the senderDeviceId so bot entries live in a distinct
        // namespace from user→user keys (which use bare device UUIDs). Row id:
        // `${channelId}:${senderId}:bot:${keyId}`. No force:true needed — unique
        // namespace means there is no collision to override.
        const botDeviceId = `bot:${p.keyId}`;
        const saveObj: StoredPeerSenderKey = {
          id: `${p.channelId}:${senderId}:${botDeviceId}`,
          channelId: p.channelId,
          senderId,
          senderDeviceId: botDeviceId,   // "bot:<keyId>" keeps bot entries separate
          senderKey: p.key,
          keyId: 0,                      // not used for HKDF-based bot keys; 0 as sentinel
          iteration: p.iter,
        };
        await savePeerSenderKey(saveObj);
        e2eeLog.info(
          "Stored bot channel sender key from", senderId, "device", senderDeviceId,
          "channel", p.channelId, "slotDeviceId", botDeviceId, "iter", p.iter,
        );
        // Task 2: confirm receipt of the bot sender key. The server recorded
        // the pending row with senderDeviceId = the bot's REAL device id
        // (== envelope.senderDeviceId, the `senderDeviceId` param here) and
        // keyId = botAckKeyId(<generation UUID>). We compute the SAME int from
        // the inbound envelope's UUID (p.keyId) so the ack matches the row.
        try {
          const { queueDeliveryAck, botAckKeyId } = await import("./useCrypto-group");
          queueDeliveryAck({
            channelId: p.channelId,
            senderDeviceId,
            keyId: botAckKeyId(p.keyId),
          });
        } catch { /* non-fatal */ }
        // Notify chat to re-decrypt messages that were waiting for this key
        window.dispatchEvent(new CustomEvent("sender-key-received", { detail: { channelId: p.channelId } }));
        return;
      }
    }
  } catch {
    // Not a JSON envelope with our discriminator — fall through to sender-key path
  }

  try {
    const senderKeyData = JSON.parse(plaintext) as {
      channelId: string;
      senderKey: string;
      keyId: number;
      iteration?: number;
      recovery?: boolean;
    };
    // History-recovery signal: sender transmitted the iter=0 seed so we can
    // derive any past iteration. Store it as `seed` alongside the ratchet state
    // so decryptGroupMessage can ratchet deterministically from iter=0 for each
    // incoming message, avoiding replay-rejections on out-of-order history.
    const isSeedRecovery = senderKeyData.recovery === true && (senderKeyData.iteration ?? 0) === 0;
    // Matrix-style firstKnown application — look up any existing slot so we
    // preserve a *lower* firstKnownIndex (better history coverage) and the
    // accumulated seenIters set. Orthogonal to seed-mode: both fields can
    // coexist on the same record.
    const existing = await loadPeerSenderKey(senderKeyData.channelId, senderId, senderDeviceId);
    const saveObj: StoredPeerSenderKey = {
      id: `${senderKeyData.channelId}:${senderId}:${senderDeviceId}`,
      channelId: senderKeyData.channelId,
      senderId,
      senderDeviceId,
      senderKey: senderKeyData.senderKey,
      ...(isSeedRecovery ? { seed: senderKeyData.senderKey } : {}),
      keyId: senderKeyData.keyId,
      iteration: senderKeyData.iteration ?? 0,
    };
    // applyFirstKnown mutates the passed-in object; since saveObj is structurally
    // compatible (StoredPeerSenderKey has all required + optional fields), we can
    // pass it directly. The helper only touches firstKnownKey/Index/seenIters.
    applyFirstKnown(saveObj, existing);
    await savePeerSenderKey(saveObj, { force: senderKeyData.recovery === true });
    // Option E — best-effort server-side backup of the peer sender key.
    // Fire-and-forget; must not throw into the receive path.
    import("../lib/session-backup")
      .then(({ uploadSessionBackup }) => uploadSessionBackup(saveObj))
      .catch(() => {});
    e2eeLog.info("Stored sender key from", senderId, "device", senderDeviceId, "for channel", senderKeyData.channelId, "at iteration", senderKeyData.iteration ?? 0, senderKeyData.recovery ? "(recovery)" : "");
    // Task 2: confirm receipt so the server flips the sender_key_acks row. The
    // server keyed the pending row on (channelId, senderDeviceId, keyId) — we
    // ack with the SAME tuple (human keyId is already an integer). Best-effort:
    // queue only, the heartbeat drains it; a failure here must not break import.
    try {
      const { queueDeliveryAck } = await import("./useCrypto-group");
      queueDeliveryAck({
        channelId: senderKeyData.channelId,
        senderDeviceId,
        keyId: senderKeyData.keyId,
      });
    } catch { /* non-fatal — ack will be re-derivable on next import/heartbeat */ }
    // Notify chat to re-decrypt messages that were waiting for this key
    window.dispatchEvent(new CustomEvent("sender-key-received", { detail: { channelId: senderKeyData.channelId } }));
  } catch (err) {
    e2eeLog.warn("Failed to process sender key distribution:", err);
  }
}

export async function decryptMessage(
  senderId: string,
  envelopeStr: string,
  get: () => CryptoState,
  set: (fn: (s: CryptoState) => Partial<CryptoState>) => void,
): Promise<string | null> {
  const state = get();
  if (!state.deviceKeys || !state.deviceId) {
    return null;
  }

  // Parse envelope — if malformed, skip silently
  let envelope: E2EEEnvelope;
  try {
    envelope = JSON.parse(envelopeStr) as E2EEEnvelope;
  } catch {
    return null;
  }
  if (envelope.v !== 1) return null;

  // One-shot X3DH sender key distribution — independent of DM session.
  // Errors MUST propagate to callers: pending-fetch / bulk-login paths rely on
  // throw to decide whether to ACK. Swallowing caused stale envelopes (pre-key
  // already consumed on WS push) to be silently ACKed + deleted while no state
  // update happened, wedging recovery.
  if (envelope.type === "sender_key_x3dh") {
    const payloadStr = new TextDecoder().decode(fromBase64(envelope.payload));
    const innerMessage = JSON.parse(payloadStr) as {
      type: string;
      usedSignedPreKeyId: number;
      usedPreKeyId?: number;
      usedSignedPreKeyPubSnippet?: string;
      senderIdentityKey?: string;
      ephemeralKey?: string;
      message?: string;
    };

    if (innerMessage.type === "initial") {
      const { preKey, deviceKeys: dk, recovered: opkRecovered } =
        await resolvePreKeyWithRefresh(state.deviceKeys, innerMessage.usedPreKeyId, loadFreshDeviceKeys);
      if (opkRecovered) {
        set(() => ({ deviceKeys: dk })); // heal the stale in-memory snapshot
        reportE2eeError({
          phase: "x3dh_opk_idb_recovered",
          senderId,
          senderDeviceId: envelope.senderDeviceId,
          deviceId: state.deviceId,
          error: `opk_recovered_from_idb usedPK=${innerMessage.usedPreKeyId}`,
        });
      }
      const localPreKeyIds = state.deviceKeys.preKeys.map((pk) => pk.keyId);
      const localIdkSnippet =
        toBase64(state.deviceKeys.identityKeyPair.curve25519.publicKey).slice(0, 12) + "…";
      const senderIdkSnippet = typeof innerMessage.senderIdentityKey === "string"
        ? innerMessage.senderIdentityKey.slice(0, 12) + "…"
        : null;
      // F22b D1: compute local SPK pub snippet for drift detection.
      const localSPKPubSnippet = toBase64(state.deviceKeys.signedPreKey.keyPair.publicKey).slice(0, 16) + "…";
      // F22b D2: sender now embeds usedSignedPreKeyPubSnippet in the initial
      // message (first 16 chars of the SPK pub it fetched from the server).
      const senderUsedSPKPubSnippet = typeof innerMessage.usedSignedPreKeyPubSnippet === "string"
        ? innerMessage.usedSignedPreKeyPubSnippet
        : null;
      const spkPubMatch = senderUsedSPKPubSnippet !== null
        ? localSPKPubSnippet.startsWith(senderUsedSPKPubSnippet)
        : null; // null = sender did not include the field (old client)
      // F18c: structured trace before X3DH derives the shared secret. If this
      // is followed by an `aes/gcm: invalid ghash tag` it pinpoints which
      // bundle slot diverged (OPK consumed vs missing locally, SPK rotation,
      // identity-key mismatch).
      e2eeLog.log("[F18c] sender_key_x3dh.receive.pre", {
        senderId,
        senderDeviceId: envelope.senderDeviceId,
        usedPreKeyId: innerMessage.usedPreKeyId,
        usedSignedPreKeyId: innerMessage.usedSignedPreKeyId,
        hasLocalPreKey: !!preKey,
        localSignedPreKeyId: state.deviceKeys.signedPreKey.keyId,
        localPreKeyCount: localPreKeyIds.length,
        localPreKeyIdsSample: localPreKeyIds.slice(0, 5),
        localIdkSnippet,
        senderIdkSnippet,
      });
      // F22b D1: additional SPK pub snippet trace — distinguishes SPK-keyId
      // collision (same keyId, different key material) from OPK/IDK issues.
      e2eeLog.log("[F22b] sender_key_x3dh.spk_check", {
        localSPKPubSnippet,
        senderUsedSPKPubSnippet,
        spkPubMatch,
        usedSPKId: innerMessage.usedSignedPreKeyId,
        localSPKId: state.deviceKeys.signedPreKey.keyId,
        spkIdMatch: innerMessage.usedSignedPreKeyId === state.deviceKeys.signedPreKey.keyId,
        localIdkPub: toBase64(state.deviceKeys.identityKeyPair.curve25519.publicKey).slice(0, 16) + "…",
        senderClaimedIdk: typeof innerMessage.senderIdentityKey === "string"
          ? innerMessage.senderIdentityKey.slice(0, 16) + "…"
          : null,
      });

      // Fix #1: select the exact SPK the sender used, across our retained history.
      // With device-unique SPK keyIds this is the authoritative discriminator;
      // it subsumes the F22c pub-snippet gate. A keyId we never held ⟹ foreign /
      // mis-delivered wrap or stale-bundle drift → skip + self-heal (do NOT log
      // phase='x3dh_receive', which the F19e cascade-detector counts).
      const usedSpk = findSignedPreKeyById(dk, innerMessage.usedSignedPreKeyId);
      if (!usedSpk) {
        e2eeLog.warn(
          "[F22c] sender_key_x3dh wrap-guard: unknown SPK keyId — skipping undecryptable wrap, self-healing",
          {
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            usedSignedPreKeyId: innerMessage.usedSignedPreKeyId,
            localSPKId: state.deviceKeys.signedPreKey.keyId,
            // NB: computed against the CURRENT SPK only — on an unknown keyId the
            // sender may have used a (now-rotated) history pub, so false here does
            // not mean the wrap was undecryptable before rotation.
            spkPubMatchCurrentOnly: spkPubMatch,
          },
        );
        reportE2eeError({
          phase: "x3dh_spk_drift_skipped", // ≠ 'x3dh_receive' → invisible to the cascade-detector
          senderId,
          senderDeviceId: envelope.senderDeviceId,
          deviceId: state.deviceId,
          error: `spk_keyid_unknown usedSPK=${innerMessage.usedSignedPreKeyId} localSPK=${state.deviceKeys.signedPreKey.keyId} historyIds=${(state.deviceKeys.previousSignedPreKeys ?? []).map((s) => s.keyId).join(",")} localSPKPub=${localSPKPubSnippet} senderUsedSPKPub=${senderUsedSPKPubSnippet}`,
        });
        const { triggerDriftSelfHeal } = await import("./useCrypto");
        void triggerDriftSelfHeal();
        return null;
      }

      let result;
      try {
        result = receiveSession(
          dk.identityKeyPair,
          usedSpk.keyPair,
          preKey,
          envelope.payload,
        );
      } catch (x3dhErr) {
        e2eeLog.error("[F18c] sender_key_x3dh.receive.fail", {
          senderId,
          senderDeviceId: envelope.senderDeviceId,
          usedPreKeyId: innerMessage.usedPreKeyId,
          usedSignedPreKeyId: innerMessage.usedSignedPreKeyId,
          hasLocalPreKey: !!preKey,
          localSignedPreKeyId: state.deviceKeys.signedPreKey.keyId,
          // F22b D1: include SPK pub snippets in error log for drift detection
          localSPKPubSnippet,
          senderUsedSPKPubSnippet,
          spkPubMatch,
          err: String(x3dhErr),
        });
        // Migration-tail follow-up: a LEGACY SPK keyId (< SPK_ID_MIN_UNIQUE — the
        // old hardcoded 1) that ghash-fails is the pre-migration collision tail.
        // The sender used a DIFFERENT key under that non-unique id (or is an
        // old-bundle sender with no pub snippet), so decryption is deterministically
        // impossible. Route to the NON-counting skip phase + self-heal instead of
        // feeding x3dh_receive (which the F19e cascade-detector counts) and
        // re-throwing. Unique-id (>= SPK_ID_MIN_UNIQUE) failures stay on x3dh_receive
        // — those are genuine held-SPK failures worth detecting.
        //
        // Deliberate exception to the "errors MUST propagate" contract at the top of
        // this branch: that contract guards RETRY-recoverable failures (a pre-key
        // consumed on WS push that would decrypt on the next attempt). A legacy-keyId
        // collision is NEVER retry-decryptable (wrong key material), so ACK+drop
        // (return null) is correct here — recovery comes from the self-heal republish
        // + sender redistribute against a unique id, not from re-delivering this wrap.
        // Stale-bundle / consumed-OPK guard: the sender used a one-time pre-key we
        // no longer hold (usedPK is set but absent from our pool AND not our
        // fallback). That means the sender built X3DH from a STALE/cached bundle, or
        // the OPK was already consumed — NOT local device corruption. Reporting it as
        // x3dh_receive gets the device wrongly cascade-deactivated (the 2026-06-25
        // zombie churn). Skip (non-counting phase) + ask the sender to reset so it
        // refetches our current bundle with a fresh OPK. NB: usedPK === undefined is
        // SPK-only X3DH (no OPK), where a failure IS a genuine SPK/identity problem —
        // those fall through to x3dh_receive below.
        if (innerMessage.usedPreKeyId !== undefined && !preKey) {
          reportE2eeError({
            phase: "x3dh_stale_bundle_skipped",
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            deviceId: state.deviceId,
            error: `opk_missing usedPK=${innerMessage.usedPreKeyId} usedSPK=${innerMessage.usedSignedPreKeyId} localSPK=${state.deviceKeys.signedPreKey.keyId} spkPubMatch=${spkPubMatch} localPKCount=${localPreKeyIds.length} err=${String(x3dhErr)}`,
          });
          // Nudge the sender to rebuild against our current bundle (fresh OPK).
          requestSessionReset(senderId, get);
          return null;
        }
        if (innerMessage.usedSignedPreKeyId < SPK_ID_MIN_UNIQUE) {
          reportE2eeError({
            phase: "x3dh_spk_drift_skipped",
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            deviceId: state.deviceId,
            error: `spk_keyid_collision usedSPK=${innerMessage.usedSignedPreKeyId} localSPK=${state.deviceKeys.signedPreKey.keyId} spkPubMatch=${spkPubMatch} err=${String(x3dhErr)}`,
          });
          const { triggerDriftSelfHeal } = await import("./useCrypto");
          void triggerDriftSelfHeal();
          return null;
        }
        // P1.4: SPK-pub bucketing — only count in the cascade-detector when the sender
        // provably used our CURRENT SPK and X3DH still failed (genuine local corruption).
        //
        // spkIsHistorical: usedSignedPreKeyId maps to a previousSignedPreKey, not the
        // current one (findSignedPreKeyById returned it from history).  The sender built
        // X3DH from a stale/cached bundle before our SPK rotation → not local corruption.
        // spkPubMatch===false is a secondary safety net (same conclusion, different signal).
        // spkPubMatch===null: old client, no snippet → ambiguous → conservative skip.
        const spkIsHistorical = innerMessage.usedSignedPreKeyId !== dk.signedPreKey.keyId;
        if (spkIsHistorical || spkPubMatch === false) {
          reportE2eeError({
            phase: "x3dh_stale_spk_skipped",
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            deviceId: state.deviceId,
            error: `sender_key_x3dh stale_spk historical=${spkIsHistorical} usedSPK=${innerMessage.usedSignedPreKeyId} localSPK=${dk.signedPreKey.keyId} spkPubMatch=${spkPubMatch} err=${String(x3dhErr)}`,
          });
          requestSessionReset(senderId, get);
          return null;
        }
        if (spkPubMatch === null) {
          // Old client — sender did not embed usedSignedPreKeyPubSnippet.  Cannot confirm
          // stale vs genuine; conservatively skip the cascade-detector so healthy devices
          // are not mis-flagged by peers running old clients.
          reportE2eeError({
            phase: "x3dh_nosnippet_skipped",
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            deviceId: state.deviceId,
            error: `sender_key_x3dh no_snippet usedSPK=${innerMessage.usedSignedPreKeyId} localSPK=${dk.signedPreKey.keyId} err=${String(x3dhErr)}`,
          });
          requestSessionReset(senderId, get);
          return null;
        }
        // spkPubMatch === true: sender provably used our CURRENT SPK and X3DH still
        // failed → genuine local corruption → x3dh_receive (server cascade-detector
        // counts it) + rethrow so the caller does NOT ACK (retry after self-heal).
        reportE2eeError({
          phase: "x3dh_receive",
          senderId,
          senderDeviceId: envelope.senderDeviceId,
          deviceId: state.deviceId,
          // F22b D1: include localSPKPub in error string for server-side diagnosis
          error: `sender_key_x3dh usedPK=${innerMessage.usedPreKeyId} usedSPK=${innerMessage.usedSignedPreKeyId} hasLocalPK=${!!preKey} localSPK=${state.deviceKeys.signedPreKey.keyId} localSPKPub=${localSPKPubSnippet} senderUsedSPKPub=${senderUsedSPKPubSnippet ?? "n/a"} spkPubMatch=${spkPubMatch} localPKCount=${localPreKeyIds.length} localIdk=${localIdkSnippet} senderIdk=${senderIdkSnippet} err=${String(x3dhErr)}`,
        });
        throw x3dhErr;
      }

      // Remove consumed pre-key
      if (innerMessage.usedPreKeyId !== undefined) {
        // Fix 3: a sender used our last-resort key → our OPK pool was empty.
        // Record it and let replenishment top the pool back up so the fallback
        // is only ever a transient bridge.
        if (innerMessage.usedPreKeyId === FALLBACK_PREKEY_ID) {
          reportE2eeError({
            phase: "lrk_used",
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            deviceId: state.deviceId,
            error: `fallback_prekey_consumed outer=sender_key_x3dh`,
          });
        }
        await removeLocalPreKey(innerMessage.usedPreKeyId, get, set);
        get().checkAndReplenishPreKeys().catch(() => {});
      }

      // Session is NOT saved — one-shot, no impact on DM session
      await processSenderKeyPlaintext(senderId, envelope.senderDeviceId, result.plaintext);
      e2eeLog.info("Processed sender_key_x3dh from", senderId, "device", envelope.senderDeviceId);
      e2eeLog.log("[F18c] sender_key_x3dh.receive.ok", {
        senderId,
        senderDeviceId: envelope.senderDeviceId,
        usedPreKeyId: innerMessage.usedPreKeyId,
      });
    }
    return null;
  }

  try {
    if (envelope.type === "message" || envelope.type === "sender_key") {
      return await withSessionLock(senderId, envelope.senderDeviceId, async (): Promise<string | null> => {
      const sessionKey = `${senderId}:${envelope.senderDeviceId}`;

      // Try existing session first
      let session: CryptoSession | undefined = state.sessions.get(sessionKey);
      e2eeLog.log("Decrypt attempt:", { sessionKey, hasInMemory: !!session });

      if (!session) {
        // Try to restore from IndexedDB
        const stored = await loadSession(senderId, envelope.senderDeviceId);
        e2eeLog.log("IndexedDB lookup:", { senderId, deviceId: envelope.senderDeviceId, found: !!stored });
        if (stored) {
          const ratchetState = JSON.parse(stored.ratchetState) as SerializedRatchetState;
          const ad = fromBase64(stored.associatedData);
          const remoteIdentityKey = fromBase64(stored.remoteIdentityKey);
          e2eeLog.log("Restoring session from IDB, AD length:", ad.length, "ratchet msgNum:", ratchetState.receivingMessageNumber);
          session = restoreSession(ratchetState, ad, remoteIdentityKey);

          set((s) => {
            const sessions = new Map(s.sessions);
            sessions.set(sessionKey, session!);
            return { sessions };
          });
        }
      }

      if (session) {
        // Existing session — decrypt as regular message
        try {
          const sessionState = session.exportState();
          e2eeLog.log("Session state before decrypt:", {
            sendingMsgNum: sessionState.sendingMessageNumber,
            receivingMsgNum: sessionState.receivingMessageNumber,
            hasSendingChain: !!sessionState.sendingChainKey,
            hasReceivingChain: !!sessionState.receivingChainKey,
            hasDhReceiving: !!sessionState.dhReceivingKey,
            dhSendingPub: sessionState.dhSendingPublic?.slice(0, 16) + "...",
            dhReceiving: sessionState.dhReceivingKey?.slice(0, 16) + "...",
          });
          // Also log what the incoming message looks like
          try {
            const payloadStr = new TextDecoder().decode(fromBase64(envelope.payload));
            const inner = JSON.parse(payloadStr);
            e2eeLog.log("Incoming message:", {
              dhPublicKey: inner.dhPublicKey?.slice(0, 16) + "...",
              messageNumber: inner.messageNumber,
              previousChainLength: inner.previousChainLength,
            });
          } catch { /* ignore */ }
          const plaintext = session.decrypt(envelope.payload);
          e2eeLog.log("✅ Decrypted successfully");

          // P0.2: Persist-before-ack — the ratchet state MUST be committed to IDB
          // before returning plaintext. A crash or tab-close between decrypt() and
          // this await means IDB retains the pre-decrypt state; the next inbound
          // message will fail AEAD authentication on the already-advanced chain,
          // cascading into session deletion and an X3DH reset.
          // Runs inside withSessionLock so no sibling tab can race on the same key.
          const serializedState = session.exportState();
          const storedRecord = await loadSession(senderId, envelope.senderDeviceId);
          if (storedRecord) {
            storedRecord.ratchetState = JSON.stringify(serializedState);
            storedRecord.updatedAt = new Date().toISOString();
            // F15d: liveness signal — peer just successfully encrypted to us
            // under this ratchet, so it is unambiguously alive. Updated only
            // on successful decrypt to avoid false-positives from ratchet-
            // saves that happen after merely producing a ciphertext.
            storedRecord.peerLastSeen = new Date().toISOString();
            try {
              await saveSessionOrThrow(storedRecord);
            } catch (saveErr) {
              // IDB quota / timeout: message delivery takes priority over strict
              // persistence. Return plaintext, but surface the failure so the
              // ratchet-state drift is visible in telemetry rather than silent.
              // Do NOT delete the session — it still holds the correct in-memory
              // state for subsequent operations within this tab's lifetime.
              e2eeLog.error("[P0.2] CRITICAL: session state NOT persisted after decrypt — ratchet drift risk", { sessionKey, err: String(saveErr) });
              reportE2eeError({
                phase: "session_persist_fail",
                senderId,
                senderDeviceId: envelope.senderDeviceId,
                deviceId: state.deviceId,
                error: `post_decrypt_save_failed: ${String(saveErr).slice(0, 200)}`,
              });
            }
          } else {
            // In-memory session with no IDB record: saveSession failed silently
            // during session creation (IDB was full or unavailable at that time).
            // Associated data is not accessible here so the record cannot be
            // reconstructed — log loudly so the gap is visible in telemetry.
            e2eeLog.error("[P0.2] existing in-memory session has no IDB record — ratchet state cannot be persisted", { sessionKey });
            reportE2eeError({
              phase: "session_idb_miss",
              senderId,
              senderDeviceId: envelope.senderDeviceId,
              deviceId: state.deviceId,
              error: `in_memory_session_no_idb_record sessionKey=${sessionKey}`,
            });
          }

          // If this was a sender_key distribution, store the key and skip display
          if (envelope.type === "sender_key" && plaintext) {
            await processSenderKeyPlaintext(senderId, envelope.senderDeviceId, plaintext);
            return null;
          }

          return plaintext;
        } catch (decryptErr) {
          const failState = session.exportState();
          e2eeLog.error("❌ Session decrypt failed:", decryptErr);
          e2eeLog.warn("Session state at failure:", JSON.stringify({
            sendMN: failState.sendingMessageNumber,
            recvMN: failState.receivingMessageNumber,
            hasSendChain: !!failState.sendingChainKey,
            hasRecvChain: !!failState.receivingChainKey,
            hasDhRecv: !!failState.dhReceivingKey,
            dhSendPub: failState.dhSendingPublic?.slice(0, 20),
            dhRecv: failState.dhReceivingKey?.slice(0, 20),
          }));
          // Log the incoming message details
          try {
            const payloadStr2 = new TextDecoder().decode(fromBase64(envelope.payload));
            const inner2 = JSON.parse(payloadStr2);
            e2eeLog.warn("Failed message details:", JSON.stringify({
              dhPub: inner2.dhPublicKey?.slice(0, 20),
              msgNum: inner2.messageNumber,
              prevChain: inner2.previousChainLength,
            }));
          } catch { /* ignore */ }
          // Delete the broken session so next encrypt triggers fresh X3DH
          e2eeLog.warn("Deleting broken session for", sessionKey);
          set((s) => {
            const sessions = new Map(s.sessions);
            sessions.delete(sessionKey);
            return { sessions };
          });
          await deleteSession(senderId, envelope.senderDeviceId);

          // Auto session reset: notify the sender to rebuild their session for our device
          requestSessionReset(senderId, get);

          // Runtime corruption signal: a session decrypt failure (ghash) is a
          // per-peer event, but failures across MANY distinct senders mean our
          // local state is corrupt → maybe flag the device broken.
          void recordDecryptFailureAndMaybeFlagBroken(senderId, get, set);

          // Fall through to X3DH check below
        }
      }

      // No existing session — check if payload is an initial message (X3DH)
      // F18c: declare diagnostic context at outer scope so the catch can
      // attach it to the telemetry report.
      let f18cUsedPK: unknown = undefined;
      let f18cUsedSPK: unknown = undefined;
      let f18cHasLocalPK = false;
      let f18cLocalSPK: unknown = undefined;
      let f18cLocalPKCount = 0;
      let f18cLocalIdk: string | null = null;
      let f18cSenderIdk: string | null = null;
      // P1.4: SPK-pub bucketing — computed before receiveSession so the catch can use them.
      let f18cSpkIsHistorical = false;      // usedSignedPreKeyId found in previousSignedPreKeys
      let f18cSpkPubMatch: boolean | null = null; // null = old client (no snippet)
      try {
        const payloadStr = new TextDecoder().decode(fromBase64(envelope.payload));
        const innerMessage = JSON.parse(payloadStr) as {
          type: string;
          usedSignedPreKeyId: number;
          usedPreKeyId?: number;
          usedSignedPreKeyPubSnippet?: string;
          senderIdentityKey: string; // always present in an X3DH initial message
          ephemeralKey?: string;
          message?: string;
        };

        if (innerMessage.type === "initial") {
          // This is an initial message with X3DH data
          const { preKey, deviceKeys: dk2, recovered: opkRecovered2 } =
            await resolvePreKeyWithRefresh(state.deviceKeys!, innerMessage.usedPreKeyId, loadFreshDeviceKeys);
          if (opkRecovered2) {
            set(() => ({ deviceKeys: dk2 })); // heal the stale in-memory snapshot
            reportE2eeError({
              phase: "x3dh_opk_idb_recovered",
              senderId,
              senderDeviceId: envelope.senderDeviceId,
              deviceId: state.deviceId,
              error: `dm_initial opk_recovered_from_idb usedPK=${innerMessage.usedPreKeyId}`,
            });
          }
          const localPreKeyIds2 = state.deviceKeys!.preKeys.map(pk => pk.keyId);
          const localIdkSnippet2 =
            toBase64(state.deviceKeys!.identityKeyPair.curve25519.publicKey).slice(0, 12) + "…";
          const senderIdkSnippet2 = typeof innerMessage.senderIdentityKey === "string"
            ? innerMessage.senderIdentityKey.slice(0, 12) + "…"
            : null;
          f18cUsedPK = innerMessage.usedPreKeyId;
          f18cUsedSPK = innerMessage.usedSignedPreKeyId;
          f18cHasLocalPK = !!preKey;
          f18cLocalSPK = state.deviceKeys!.signedPreKey.keyId;
          f18cLocalPKCount = localPreKeyIds2.length;
          f18cLocalIdk = localIdkSnippet2;
          f18cSenderIdk = senderIdkSnippet2;
          e2eeLog.log("X3DH receiveSession:", {
            senderDeviceId: envelope.senderDeviceId,
            usedPreKeyId: innerMessage.usedPreKeyId,
            usedSignedPreKeyId: innerMessage.usedSignedPreKeyId,
            hasPreKey: !!preKey,
            localPreKeyIds: localPreKeyIds2,
            localSignedPreKeyId: state.deviceKeys!.signedPreKey.keyId,
          });
          // F18c: same structured pre-trace as sender_key_x3dh path so failures
          // are diagnosable identically across both X3DH receive entrypoints.
          e2eeLog.log("[F18c] x3dh_initial.receive.pre", {
            envelopeType: envelope.type,
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            usedPreKeyId: innerMessage.usedPreKeyId,
            usedSignedPreKeyId: innerMessage.usedSignedPreKeyId,
            hasLocalPreKey: !!preKey,
            localSignedPreKeyId: state.deviceKeys!.signedPreKey.keyId,
            localPreKeyCount: localPreKeyIds2.length,
            localPreKeyIdsSample: localPreKeyIds2.slice(0, 5),
            localIdkSnippet: localIdkSnippet2,
            senderIdkSnippet: senderIdkSnippet2,
          });
          const usedSpk2 = findSignedPreKeyById(dk2, innerMessage.usedSignedPreKeyId);
          if (!usedSpk2) {
            e2eeLog.warn(
              "[F22c] x3dh_initial wrap-guard: unknown SPK keyId — skipping undecryptable wrap, self-healing",
              {
                senderId,
                senderDeviceId: envelope.senderDeviceId,
                usedSignedPreKeyId: innerMessage.usedSignedPreKeyId,
                localSPKId: state.deviceKeys!.signedPreKey.keyId,
              },
            );
            reportE2eeError({
              phase: "x3dh_spk_drift_skipped",
              senderId,
              senderDeviceId: envelope.senderDeviceId,
              deviceId: state.deviceId,
              error: `dm_initial spk_keyid_unknown usedSPK=${innerMessage.usedSignedPreKeyId} localSPK=${state.deviceKeys!.signedPreKey.keyId} historyIds=${(state.deviceKeys!.previousSignedPreKeys ?? []).map((s) => s.keyId).join(",")}`,
            });
            const { triggerDriftSelfHeal } = await import("./useCrypto");
            void triggerDriftSelfHeal();
            return null;
          }
          // P1.4: compute SPK-pub match and historical flag BEFORE receiveSession so the
          // catch block has correct values if receiveSession throws.
          const f18cSPKPubLocal = toBase64(dk2.signedPreKey.keyPair.publicKey).slice(0, 16) + "…";
          const f18cSPKPubSender = typeof innerMessage.usedSignedPreKeyPubSnippet === "string"
            ? innerMessage.usedSignedPreKeyPubSnippet : null;
          f18cSpkPubMatch = f18cSPKPubSender !== null
            ? f18cSPKPubLocal.startsWith(f18cSPKPubSender) : null;
          f18cSpkIsHistorical = innerMessage.usedSignedPreKeyId !== dk2.signedPreKey.keyId;
          const result = receiveSession(
            dk2.identityKeyPair,
            usedSpk2.keyPair,
            preKey,
            envelope.payload,
          );

          // Remove consumed pre-key from local store
          if (innerMessage.usedPreKeyId !== undefined) {
            // Fix 3: fallback / last-resort key consumed → OPK pool was empty.
            if (innerMessage.usedPreKeyId === FALLBACK_PREKEY_ID) {
              reportE2eeError({
                phase: "lrk_used",
                senderId,
                senderDeviceId: envelope.senderDeviceId,
                deviceId: state.deviceId,
                error: `fallback_prekey_consumed outer=${envelope.type}`,
              });
            }
            await removeLocalPreKey(innerMessage.usedPreKeyId, get, set);
            // Check if we need to replenish pre-keys
            get().checkAndReplenishPreKeys().catch(() => {});
          }

          // Persist new session
          const serializedState = result.session.exportState();
          const ad = new Uint8Array([
            ...fromBase64(innerMessage.senderIdentityKey),
            ...state.deviceKeys!.identityKeyPair.curve25519.publicKey,
          ]);

          // P0.2: Persist-before-ack — new session state must reach IDB before the
          // plaintext (and the consumed OPK removal above) are treated as committed.
          const x3dhRecord = {
            peerId: senderId,
            peerDeviceId: envelope.senderDeviceId,
            ratchetState: JSON.stringify(serializedState),
            associatedData: toBase64(ad),
            remoteIdentityKey: innerMessage.senderIdentityKey,
            updatedAt: new Date().toISOString(),
            // F15d: X3DH initial decrypt succeeded — peer is alive right now.
            peerLastSeen: new Date().toISOString(),
          };
          try {
            await saveSessionOrThrow(x3dhRecord);
          } catch (saveErr) {
            e2eeLog.error("[P0.2] CRITICAL: X3DH session NOT persisted after initial decrypt — ratchet drift risk", { sessionKey, err: String(saveErr) });
            reportE2eeError({
              phase: "session_persist_fail",
              senderId,
              senderDeviceId: envelope.senderDeviceId,
              deviceId: state.deviceId,
              error: `x3dh_initial_save_failed: ${String(saveErr).slice(0, 200)}`,
            });
            // Return plaintext: the session is added to in-memory state below.
            // Subsequent decrypts within this tab will work; after a reload the
            // session is gone and will require X3DH reset.
          }

          set((s) => {
            const sessions = new Map(s.sessions);
            sessions.set(sessionKey, result.session);
            return { sessions };
          });

          // If this was a sender_key distribution, store the key and skip display
          if (envelope.type === "sender_key" && result.plaintext) {
            await processSenderKeyPlaintext(senderId, envelope.senderDeviceId, result.plaintext);
            return null;
          }

          return result.plaintext;
        }
      } catch (x3dhErr) {
        e2eeLog.error("❌ X3DH receiveSession failed:", x3dhErr);
        e2eeLog.error("[F18c] x3dh_initial.receive.fail", {
          envelopeType: envelope.type,
          senderId,
          senderDeviceId: envelope.senderDeviceId,
          usedPreKeyId: f18cUsedPK,
          usedSignedPreKeyId: f18cUsedSPK,
          hasLocalPreKey: f18cHasLocalPK,
          localSignedPreKeyId: f18cLocalSPK,
          err: String(x3dhErr),
        });
        // Naked fetch here previously bypassed the api helper and never set the
        // Authorization header — the requireAuth preHandler 401'd every report.
        // Route through reportE2eeError so the Bearer token is attached and the
        // 30s in-memory dedupe applies.
        // Migration-tail follow-up (same as the sender_key_x3dh site): a legacy
        // keyId (< SPK_ID_MIN_UNIQUE) that ghash-fails is the pre-migration
        // collision tail → route to the non-counting skip phase + self-heal instead
        // of feeding x3dh_receive. f18cUsedSPK is `unknown` here, so guard the type.
        // Stale-bundle / consumed-OPK guard (same as the sender_key_x3dh site): the
        // sender used a one-time pre-key we no longer hold → stale/cached bundle, not
        // device corruption. Skip (non-counting) + request a session reset so the
        // sender refetches a fresh OPK, instead of feeding x3dh_receive → cascade.
        // f18cUsedPK is `unknown`; undefined = SPK-only X3DH (genuine) → fall through.
        if (f18cUsedPK !== undefined && f18cHasLocalPK === false) {
          reportE2eeError({
            phase: "x3dh_stale_bundle_skipped",
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            deviceId: state.deviceId,
            error: `dm_initial opk_missing usedPK=${String(f18cUsedPK)} usedSPK=${String(f18cUsedSPK)} localSPK=${String(f18cLocalSPK)} err=${String(x3dhErr)}`,
          });
          requestSessionReset(senderId, get);
          return null;
        }
        if (typeof f18cUsedSPK === "number" && f18cUsedSPK < SPK_ID_MIN_UNIQUE) {
          reportE2eeError({
            phase: "x3dh_spk_drift_skipped",
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            deviceId: state.deviceId,
            error: `dm_initial spk_keyid_collision usedSPK=${String(f18cUsedSPK)} localSPK=${String(f18cLocalSPK)} err=${String(x3dhErr)}`,
          });
          const { triggerDriftSelfHeal } = await import("./useCrypto");
          void triggerDriftSelfHeal();
          return null;
        }
        // P1.4: SPK-pub bucketing (mirrors the sender_key_x3dh path above).
        // f18cSpkIsHistorical: usedSignedPreKeyId found in previousSignedPreKeys → stale bundle.
        // f18cSpkPubMatch===false: pub snippet proves sender used a different/old SPK → stale.
        // f18cSpkPubMatch===null: old client, no snippet → conservative skip (not counted).
        if (f18cSpkIsHistorical || f18cSpkPubMatch === false) {
          reportE2eeError({
            phase: "x3dh_stale_spk_skipped",
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            deviceId: state.deviceId,
            error: `dm_initial stale_spk historical=${f18cSpkIsHistorical} usedSPK=${String(f18cUsedSPK)} localSPK=${String(f18cLocalSPK)} spkPubMatch=${f18cSpkPubMatch} err=${String(x3dhErr)}`,
          });
          requestSessionReset(senderId, get);
          return null;
        }
        if (f18cSpkPubMatch === null) {
          // Old client — no usedSignedPreKeyPubSnippet in envelope.  Cannot confirm
          // stale vs genuine; conservatively skip the cascade-detector.
          reportE2eeError({
            phase: "x3dh_nosnippet_skipped",
            senderId,
            senderDeviceId: envelope.senderDeviceId,
            deviceId: state.deviceId,
            error: `dm_initial no_snippet usedSPK=${String(f18cUsedSPK)} localSPK=${String(f18cLocalSPK)} err=${String(x3dhErr)}`,
          });
          requestSessionReset(senderId, get);
          return null;
        }
        // f18cSpkPubMatch === true: sender provably used our CURRENT SPK → genuine
        // local corruption → x3dh_receive (server cascade-detector counts it) + flag.
        reportE2eeError({
          phase: "x3dh_receive",
          senderId,
          senderDeviceId: envelope.senderDeviceId,
          deviceId: state.deviceId,
          error: `outer=${envelope.type} usedPK=${String(f18cUsedPK)} usedSPK=${String(f18cUsedSPK)} hasLocalPK=${f18cHasLocalPK} localSPK=${String(f18cLocalSPK)} localPKCount=${f18cLocalPKCount} localIdk=${f18cLocalIdk} senderIdk=${f18cSenderIdk} err=${String(x3dhErr)}`,
        });
        // Runtime corruption signal (same rationale as the session-decrypt path).
        void recordDecryptFailureAndMaybeFlagBroken(senderId, get, set);
      }

      e2eeLog.warn("No session found for", senderId, envelope.senderDeviceId);
      return null;
      });
    }

    return null;
  } catch (err) {
    e2eeLog.warn("Decryption failed:", err);
    return null;
  }
}

// ─── DM Recovery (B) ──────────────────────────────────────────────────────────
//
// When a DM message envelope (type=message) reaches us but we cannot decrypt it
// — typically because the sender's local session is "established" but our
// matching session was never built (initial X3DH was lost or ghashed) — we ask
// the specific sender device to re-encrypt that one message and POST it as a
// fresh per-device slot. The sender does this from their local plaintext-cache
// (`getCachedPlaintext`, IDB-persistent ~1000 msgs).
//
// Per-msg retry counter caps at 3 attempts to prevent loops. Counter is in
// memory: a reload resets it, which is acceptable (reloads are user-initiated
// and the user clearly wants a fresh attempt).

const dmRecoveryAttempts = new Map<string, number>();
const DM_RECOVERY_MAX_ATTEMPTS = 3;

export function getDmRecoveryAttemptCount(msgId: string): number {
  return dmRecoveryAttempts.get(msgId) ?? 0;
}

/** Fire-and-forget: send a dm_recover_request WS event to the specific sender
 *  device. Returns true if the request was actually sent (i.e., not rate-capped). */
export function requestDmRecovery(
  msgId: string,
  channelId: string,
  senderUserId: string,
  senderDeviceId: string,
  ourDeviceId: string,
): boolean {
  const prior = dmRecoveryAttempts.get(msgId) ?? 0;
  if (prior >= DM_RECOVERY_MAX_ATTEMPTS) {
    e2eeLog.log("DM recovery exhausted for", msgId, "— giving up");
    return false;
  }
  dmRecoveryAttempts.set(msgId, prior + 1);

  import("../lib/ws").then(({ ws }) => {
    try {
      ws.send({
        type: "dm_recover_request",
        msgId,
        channelId,
        senderUserId,
        senderDeviceId,
        recipientDeviceId: ourDeviceId,
      });
      e2eeLog.log("→ dm_recover_request", { msgId, senderDeviceId, attempt: prior + 1 });
    } catch (err) {
      e2eeLog.warn("Failed to send dm_recover_request:", err);
    }
  }).catch((err) => e2eeLog.warn("Failed to import ws module:", err));
  return true;
}

// ─── Session Reset ────────────────────────────────────────────────────────────

/** Rate limit: max one session_reset per peer per 60 seconds */
const sessionResetCooldown = new Map<string, number>();

/**
 * Send a session_reset signal to the peer via WebSocket.
 * The peer's client will delete their stale session for our device
 * and re-establish via X3DH on the next message send.
 */
function requestSessionReset(peerId: string, get: () => CryptoState): void {
  const now = Date.now();
  const lastReset = sessionResetCooldown.get(peerId) ?? 0;
  if (now - lastReset < 60_000) {
    e2eeLog.log("Session reset for", peerId, "throttled (cooldown)");
    return;
  }
  sessionResetCooldown.set(peerId, now);

  const state = get();
  if (!state.deviceId) return;

  // Use the ws singleton to send the reset signal
  import("../lib/ws").then(({ ws }) => {
    try {
      ws.send({
        type: "session_reset",
        targetUserId: peerId,
        senderDeviceId: state.deviceId!,
      });
      e2eeLog.log("✅ Sent session_reset to", peerId, "for device", state.deviceId);
    } catch (err) {
      e2eeLog.warn("Failed to send session_reset:", err);
    }
  }).catch((err) => {
    e2eeLog.warn("Failed to import ws module:", err);
  });
}

// ─── Internal helper ──────────────────────────────────────────────────────────

/** Remove a consumed pre-key from local IndexedDB and in-memory state */
async function removeLocalPreKey(
  keyId: number,
  get: () => CryptoState,
  set: (fn: (s: CryptoState) => Partial<CryptoState>) => void,
): Promise<void> {
  // Fix 3: the fallback / last-resort pre-key is reused across senders and must
  // never be consumed — skip it even though X3DH reports it as usedPreKeyId.
  if (keyId === FALLBACK_PREKEY_ID) {
    e2eeLog.log("Skipping removal of fallback pre-key", keyId);
    return;
  }
  try {
    const stored = await getFirstDeviceKeys();
    if (!stored) return;
    stored.preKeys = stored.preKeys.filter((pk) => pk.keyId !== keyId);
    await saveDeviceKeys(stored);

    const state = get();
    if (state.deviceKeys) {
      const updatedKeys = {
        ...state.deviceKeys,
        preKeys: state.deviceKeys.preKeys.filter((pk) => pk.keyId !== keyId),
      };
      set(() => ({ deviceKeys: updatedKeys }));
    }
  } catch (err) {
    e2eeLog.warn("Failed to remove local pre-key:", err);
  }
}
